import { createContext, ReactNode, useContext, useState, useMemo, Dispatch, SetStateAction } from 'react';
import dayjs from 'services/dayjs';
import { UseQueryState } from 'urql';
import { useIsReportsPdfMode } from 'screens/Reports/useIsReportsPdfMode';
import {
  FilterFragment as Filter,
  FilterInput,
  FilterType,
  ReportDashboardsTabsQuery,
  useReportDashboardsTabsQuery,
} from 'generated/graphql';
import useNumberParams from './useNumberParams';
import useProfileConfig from './useProfileConfig';
import useUser from './useUser';

interface ReportsScreenContextProps {
  tabsNewResult: UseQueryState<ReportDashboardsTabsQuery>;
  tabsFetching?: boolean;
  refreshTabsNewResult: () => void;
  heroOptions: HeroOptions;
  setHeroOptions: Dispatch<SetStateAction<HeroOptions>>;
  searchInputValue: string;
  setSearchInputValue: Dispatch<SetStateAction<string>>;
  hasHeroSearch: boolean;
  setHasHeroSearch: Dispatch<SetStateAction<boolean>>;
}

export interface HeroOptions {
  afterActions: ReactNode | null;
  beforeSearch: ReactNode | null;
  afterSearch: ReactNode | null;
  mobileActions: ReactNode | null;
}

const Context = createContext<ReportsScreenContextProps | undefined>(undefined);

export const getDefaultFilters = (filters: Filter[] | null = null): FilterInput[] => {
  if (filters) {
    return filters.map((filter: Filter) => ({
      type: filter.type,
      selectedIDs: [],
    }));
  }
  return Object.values(FilterType).map((type) => ({
    type,
    selectedIDs: [],
  }));
};

export const ReportsScreenProvider = ({ children }: { children: ReactNode }) => {
  const [getNumberParam] = useNumberParams();
  const productID = getNumberParam('productID') ?? 0;
  const { user, isAdmin } = useUser();
  const { profileID } = useProfileConfig();
  const sellerID = isAdmin && profileID ? profileID : user?.profileId?.toString();
  const isReportsPdfMode = useIsReportsPdfMode();

  const start = useMemo(() => dayjs().subtract(7, 'days'), []);
  const end = useMemo(() => dayjs(), []);
  const packingDate = useMemo(() => dayjs(), []);
  const filters = useMemo(() => getDefaultFilters(null), []);

  const [tabsNewResult, refreshTabsNewResult] = useReportDashboardsTabsQuery({
    variables: {
      input: {
        start: start.toISOString(),
        end: end.toISOString(),
        search: '',
        productID,
        filters,
        shipmentReference: '',
        printerFriendly: false,
        profileID: sellerID ?? '',
      },
    },
    requestPolicy: 'network-only',
    pause: !sellerID || isReportsPdfMode,
  });

  const tabsFetching = tabsNewResult.fetching || tabsNewResult.stale;

  const [heroOptions, setHeroOptions] = useState<HeroOptions>({
    afterActions: null,
    beforeSearch: null,
    afterSearch: null,
    mobileActions: null,
  });

  const [searchInputValue, setSearchInputValue] = useState('');
  const [hasHeroSearch, setHasHeroSearch] = useState(false);

  const value = useMemo(
    () => ({
      tabsNewResult,
      tabsFetching,
      refreshTabsNewResult,
      heroOptions,
      setHeroOptions,
      searchInputValue,
      setSearchInputValue,
      hasHeroSearch,
      setHasHeroSearch,
    }),
    [
      tabsNewResult,
      tabsFetching,
      refreshTabsNewResult,
      heroOptions,
      setHeroOptions,
      searchInputValue,
      setSearchInputValue,
      hasHeroSearch,
      setHasHeroSearch,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useReportsScreen = () => {
  const result = useContext(Context);
  if (!result) throw new Error('useReportsScreen must be used within a ReportsScreenProvider');
  return result;
};
